/**
 * Dev-only mock data for home-page cards that are normally gated on
 * specific user state (active optimization windows, reported conditions,
 * uploaded test results).
 *
 * When the URL param `?dev_preview_all_cards=true` is present, these
 * mocks are injected into PlanHomeScreen so design passes can see the
 * full visual stack without manufacturing the conditions that trigger
 * each card in production.
 *
 * Mirrors the pattern of `devHelperRoleOverride.ts` and
 * `devPersonaOverride.ts`. Reads at module load, persists in
 * sessionStorage for the life of the browser tab, and is a strict no-op
 * outside `__DEV__`.
 *
 * Mock content targets the AFAB / preserve / explore default. It's
 * intentionally generic enough to read well for any persona, since the
 * goal is design preview, not behavioral testing.
 */
import type { ConditionStepContent } from "../intelligence/conditionContent";
import type { ResultInterpretation } from "../intelligence/useTestResultInsights";
import type { OptimizationWindow } from "../plan.types";
import type { TranslationFunction } from "@src/packages/i18n";

const STORAGE_KEY = "__moon_dev_preview_all_cards";

export const DEV_PREVIEW_ALL_CARDS: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_preview_all_cards",
  );
  if (fromUrl === "true" || fromUrl === "1") {
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, "1");
    } catch {
      /* sessionStorage may be disabled — fall through */
    }
    return true;
  }
  if (fromUrl === "false" || fromUrl === "0") {
    try {
      window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return false;
  }
  try {
    return window.sessionStorage?.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
})();

export const DEV_HOMEPAGE_ARCHIVE: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_home_archive",
  );
  return fromUrl === "true" || fromUrl === "1";
})();

export const MOCK_OPTIMIZATION_WINDOW: OptimizationWindow = {
  substepId: "amh",
  biologicalCycle:
    "~3 months for egg-quality interventions to show in retrieval",
  baselineAction: "Get a baseline AMH test now",
  optimizationPeriod: "2-3 months",
  retestAction: "Retest AMH before scheduling retrieval",
  lifestyle: [
    {
      title: "Prioritize sleep (7-9 hours)",
      evidence: "strong",
      impact: "high",
      education:
        "Sleep regulates the hormonal axis that governs follicle development. Consistent 7-9 hour sleep is one of the strongest modifiable factors.",
    },
    {
      title: "Mediterranean-style diet",
      evidence: "moderate",
      impact: "moderate",
      education:
        "Higher intake of olive oil, fish, legumes, and produce is associated with better embryo quality in observational studies.",
    },
  ],
  supplements: [
    {
      title: "CoQ10 (200-600 mg/day)",
      evidence: "moderate",
      impact: "moderate",
      education:
        "Supports mitochondrial function in egg cells. Most studies use 200-600 mg ubiquinol form. Start 2-3 months before retrieval.",
    },
    {
      title: "Vitamin D (if deficient)",
      evidence: "strong",
      impact: "moderate",
      education:
        "Vitamin D deficiency is linked to lower AMH and worse IVF outcomes. Test your level — supplement only if below ~30 ng/mL.",
    },
  ],
  ageGuidance: [
    {
      bracket: "<35",
      range: [0, 34],
      recommendation: "parallel",
      explanation:
        "Optimize alongside your normal timeline — don't delay testing or treatment for optimization gains at your age.",
    },
    {
      bracket: "35-37",
      range: [35, 37],
      recommendation: "parallel",
      explanation:
        "Time matters more now. Optimize and move forward simultaneously — every cycle of waiting has a real biological cost.",
    },
    {
      bracket: "38+",
      range: [38, 99],
      recommendation: "start-now-optimize-alongside",
      explanation:
        "Don't wait to optimize before starting. The biggest egg-quality gains come from getting into treatment promptly.",
    },
  ],
};

export const MOCK_CONDITION_CONTENT: ConditionStepContent = {
  condition: "pcos",
  title: "PMOS (ex PCOS) & Your Journey",
  stepId: "test",
  education:
    "PMOS (ex PCOS) affects how your ovaries respond to stimulation. You may produce more eggs than average, but there's a higher risk of ovarian hyperstimulation syndrome (OHSS). Your clinic will likely use a conservative protocol with close monitoring.",
  supplementRecommendations: [
    {
      name: "Inositol (myo + d-chiro 40:1)",
      dosage: "2g twice daily",
      evidence: "strong",
      note: "Improves ovulation and egg quality in PMOS (ex PCOS). Best evidence of any supplement for PMOS-related fertility.",
    },
  ],
};

function previewInterpretation(
  t: TranslationFunction,
  key: "amh" | "fsh" | "vitamin_d" | "tsh",
  category: ResultInterpretation["category"],
): ResultInterpretation {
  const base = `test_preview.${key}`;
  return {
    testName: t(`${base}.test_name`),
    testPurpose: t(`${base}.test_purpose`),
    value: t(`${base}.value`),
    unit: t(`${base}.unit`),
    category,
    headline: t(`${base}.headline`),
    interpretation: t(`${base}.interpretation`),
    whatThisMeans: t(`${base}.what_this_means`),
    nextStep: t(`${base}.next_step`),
  };
}

export function getMockTestInsight(
  t: TranslationFunction,
): ResultInterpretation {
  return previewInterpretation(t, "amh", "normal");
}

/**
 * Dev-mode preview array. Shown on the in-cycle home only when there are no
 * real testInsights, so designers can review how the result card handles
 * variety: different test types, different categories
 * (normal / low / high / borderline), short and long testPurpose copy.
 */
export function getMockTestInsightsPreview(
  t: TranslationFunction,
): ResultInterpretation[] {
  return [
    previewInterpretation(t, "amh", "normal"),
    previewInterpretation(t, "fsh", "high"),
    previewInterpretation(t, "vitamin_d", "low"),
    previewInterpretation(t, "tsh", "borderline"),
  ];
}
